import { IconButton } from '@atlaskit/button/new';
import { type NewCoreIconProps } from '@atlaskit/icon';
import CalculateIcon from '@atlaskit/icon-lab/core/calculate';
import SortOptionsIcon from '@atlaskit/icon-lab/core/sort-options';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import DragHandleHorizontalIcon from '@atlaskit/icon/core/drag-handle-horizontal';
import DragHandleVerticalIcon from '@atlaskit/icon/core/drag-handle-vertical';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import FieldIcon from '@atlaskit/icon/core/field';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import ShrinkDiagonalIcon from '@atlaskit/icon/core/shrink-diagonal';
import TableColumnAddLeftIcon from '@atlaskit/icon/core/table-column-add-left';
import TableColumnAddRightIcon from '@atlaskit/icon/core/table-column-add-right';
import TableRowAddAboveIcon from '@atlaskit/icon/core/table-row-add-above';
import TableRowAddBelowIcon from '@atlaskit/icon/core/table-row-add-below';
import { token } from '@atlaskit/tokens';
import { isAIFCStagingAreaView } from '@databases/database-state';
import React from 'react';
import { CreateIconComponent } from './CreateIconComponent.tsx';

export { AiSummaryIcon } from './AiSummaryIcon.tsx';

export const EntryOutlineIcon = (props: NewCoreIconProps) => <FieldIcon {...props} />;

export const EntryIconColor = (props: NewCoreIconProps) => (
  <FieldIcon color={token('color.icon.accent.purple')} {...props} />
);

export const PageTemplateIcon = (props: NewCoreIconProps) => <TemplateIcon {...props} />;

export const ExpandIcon = (props: NewCoreIconProps) => <GrowDiagonalIcon {...props} />;

export const HideIcon = (props: NewCoreIconProps) => (
  <EyeOpenStrikethroughIcon {...props} />
);

export const SortIcon = (props: NewCoreIconProps) => <SortOptionsIcon {...props} />;

export const ChangeLayoutIcon = CreateIconComponent(
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1 1.75C1 1.33579 1.33579 1 1.75 1H4.25C4.66421 1 5 1.33579 5 1.75V4.25C5 4.66421 4.66421 5 4.25 5H1.75C1.33579 5 1 4.66421 1 4.25V1.75ZM2.5 2.5V3.5H3.5V2.5H2.5ZM12.3358 2.60355C13.1168 1.8225 14.3832 1.82251 15.1642 2.60355L15.3964 2.83579C16.1775 3.61684 16.1775 4.88317 15.3964 5.66421L10.5479 10.5127C10.2687 10.7919 9.91315 10.9822 9.52597 11.0597L7.39709 11.4854C7.15119 11.5346 6.89699 11.4576 6.71967 11.2803C6.54235 11.103 6.46539 10.8488 6.51456 10.6029L6.94034 8.47403C7.01778 8.08685 7.20809 7.73125 7.48729 7.45205L12.3358 2.60355ZM14.1036 3.66421C13.9083 3.46895 13.5917 3.46895 13.3964 3.66421L13.0607 4L14 4.93934L14.3358 4.60355C14.531 4.40829 14.531 4.09171 14.3358 3.89645L14.1036 3.66421ZM12.9393 6L12 5.06066L8.54795 8.51271C8.47815 8.58251 8.43057 8.67141 8.41121 8.76821L8.20607 9.79393L9.23179 9.58879C9.32859 9.56943 9.41749 9.52185 9.48729 9.45205L12.9393 6ZM1 6.75C1 6.33579 1.33579 6 1.75 6H4.25C4.66421 6 5 6.33579 5 6.75V9.25C5 9.66421 4.66421 10 4.25 10H1.75C1.33579 10 1 9.66421 1 9.25V6.75ZM2.5 7.5V8.5H3.5V7.5H2.5ZM1 11.75C1 11.3358 1.33579 11 1.75 11H4.25C4.66421 11 5 11.3358 5 11.75V14.25C5 14.6642 4.66421 15 4.25 15H1.75C1.33579 15 1 14.6642 1 14.25V11.75ZM2.5 12.5V13.5H3.5V12.5H2.5Z"
      fill="currentColor"
    />
  </svg>
);

export const RenameViewIcon = CreateIconComponent(
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.5858 0.855317C12.3668 0.0742685 13.6332 0.0742697 14.4142 0.855318L15.1464 1.58755C15.9275 2.3686 15.9275 3.63493 15.1464 4.41598L10.0094 9.55306C9.76903 9.7934 9.47141 9.96852 9.1446 10.0619L5.95604 10.9729C5.69415 11.0477 5.41227 10.9747 5.21967 10.7821C5.02707 10.5895 4.95403 10.3076 5.02886 10.0457L5.93987 6.85717C6.03325 6.53036 6.20837 6.23273 6.44871 5.9924L11.5858 0.855317ZM13.3536 1.91598C13.1583 1.72072 12.8417 1.72072 12.6464 1.91598L12.0607 2.50176L13.5 3.9411L14.0858 3.35532C14.281 3.16006 14.281 2.84347 14.0858 2.64821L13.3536 1.91598ZM12.4393 5.00176L11 3.56242L7.50937 7.05306C7.44928 7.11314 7.4055 7.18755 7.38216 7.26925L6.84202 9.15975L8.73251 8.61961C8.81422 8.59626 8.88862 8.55248 8.94871 8.4924L12.4393 5.00176ZM3 2.50286C2.72386 2.50286 2.5 2.72672 2.5 3.00286V13.0029C2.5 13.279 2.72386 13.5029 3 13.5029H13C13.2761 13.5029 13.5 13.279 13.5 13.0029V10.0018H15V13.0029C15 14.1074 14.1046 15.0029 13 15.0029H3C1.89543 15.0029 1 14.1074 1 13.0029V3.00286C1 1.89829 1.89543 1.00286 3 1.00286H6V2.50286H3Z"
      fill="currentColor"
    />
  </svg>
);

// if you ever update the HorizontalDragHandlerIconWidth below, please also update the height too
export const HORIZ_DRAG_HANDLER_ICON_WIDTH = 24;
export const HORIZ_DRAG_HANDLER_ICON_HEIGHT = 18;

export const HorizontalDragHandlerIcon = (props: NewCoreIconProps) => (
  <DragHandleHorizontalIcon spacing="spacious" {...props} />
);

export const ActiveHorizontalDragHandlerIcon = (props: NewCoreIconProps) => (
  <DragHandleHorizontalIcon
    spacing="spacious"
    color={token('color.icon.selected')}
    {...props}
  />
);

const isInAIFCStagingAreaView = isAIFCStagingAreaView();

// if you ever update the VerticalDragHandlerIcon below, please also update the height too
export const VERT_DRAG_HANDLER_ICON_HEIGHT = isInAIFCStagingAreaView ? 20 : 25;
export const VERT_DRAG_HANDLER_ICON_WIDTH = isInAIFCStagingAreaView ? 20 : 18;
// Increased width to account for entries checkbox in left gutter
export const DRAG_HANDLER_TD_ELEMENT_WIDTH = 64;

const SmallVerticalDragHandlerIcon = (props: NewCoreIconProps) => (
  <IconButton
    icon={(iconProps) => (
      <DragHandleVerticalIcon
        {...iconProps}
        size="small"
        spacing="none"
        color="currentColor"
      />
    )}
    label={props.label}
    appearance="subtle"
  />
);

export const VerticalDragHandlerIcon = (props: NewCoreIconProps) =>
  isInAIFCStagingAreaView ? (
    <SmallVerticalDragHandlerIcon {...props} />
  ) : (
    <DragHandleVerticalIcon spacing="spacious" {...props} />
  );

export const ActiveVerticalDragHandlerIcon = (props: NewCoreIconProps) =>
  isInAIFCStagingAreaView ? (
    <SmallVerticalDragHandlerIcon {...props} />
  ) : (
    <DragHandleVerticalIcon color={token('color.icon.selected')} {...props} />
  );

export const InsertRowAboveIcon = (props: NewCoreIconProps) => (
  <TableRowAddAboveIcon {...props} />
);

export const InsertRowBelowIcon = (props: NewCoreIconProps) => (
  <TableRowAddBelowIcon {...props} />
);

export const InsertColumnBeforeIcon = (props: NewCoreIconProps) => (
  <TableColumnAddLeftIcon {...props} />
);

export const InsertColumnAfterIcon = (props: NewCoreIconProps) => (
  <TableColumnAddRightIcon {...props} />
);

export const CollapseIcon = (props: NewCoreIconProps) => (
  <ShrinkDiagonalIcon {...props} />
);

export const SummationIcon = (props: NewCoreIconProps) => <CalculateIcon {...props} />;

export { UpdatedEntryIcon } from './UpdatedEntryIcon.tsx';
